import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { UserCircle2, Mail, Calendar, Heart, MapPin, X, Loader2, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { bookingsApi, savedCardsApi, resolveMediaUrl, type BookingListResponse, type SavedCardResponse } from "@/lib/api";
import { VenueService } from "@/services/venue.service";

/* ── Profile Page ────────────────────────────────────────────────
   BACKEND ENDPOINTS:
   GET /api/v1/bookings?userId=:id → bookingsApi.myBookings() (my bookings)
   GET /api/v1/venues              → VenueService.getVenues() (favorites lookup)
   Favorites come from FavoritesContext → wishlistApi (see api.ts)
   ─────────────────────────────────────────────────────────────── */
import { Button } from "@/components/ui/button";
import { formatNaira, type Venue } from "@/data/venues";
import { VenueCard } from "@/components/venues/VenueCard";
import { toast } from "sonner";

export const Route = createFileRoute("/_user/profile")({
  component: ProfilePage,
});

type Tab = "bookings" | "favorites" | "cards" | "settings";

export function ProfilePage() {
  const { user, isAuthenticated } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("bookings");
  const [bookings, setBookings] = useState<BookingListResponse[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [savedVenues, setSavedVenues] = useState<Venue[]>([]);
  const [cards, setCards] = useState<SavedCardResponse[]>([]);
  const [cardsLoading, setCardsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) navigate({ to: "/login", search: { redirect: "/profile" } });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!user) return;
    bookingsApi.myBookings(user.id)
      .then((res) => setBookings(res.data))
      .catch(() => toast.error("Failed to load bookings"))
      .finally(() => setBookingsLoading(false));
  }, [user]);

  useEffect(() => {
    if (favorites.length === 0) { setSavedVenues([]); return; }
    Promise.all(favorites.map((id) => VenueService.getVenueById(id)))
      .then((results) => setSavedVenues(results.filter(Boolean) as Venue[]))
      .catch(() => toast.error("Failed to load favorite venues"));
  }, [favorites]);

  useEffect(() => {
    savedCardsApi.list()
      .then((res) => setCards(res))
      .catch(() => toast.error("Failed to load saved cards"))
      .finally(() => setCardsLoading(false));
  }, []);

  const setDefaultCard = async (id: string) => {
    try {
      await savedCardsApi.setDefault(id);
      setCards((prev) => prev.map((c) => ({ ...c, isDefault: c.id === id })));
      toast.success("Default card updated");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to update default card";
      toast.error(msg);
    }
  };

  const cancelBooking = async (id: string) => {
    try {
      await bookingsApi.cancel(id);
      setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status: "cancelled" } : b));
      toast.success("Booking cancelled");
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to cancel booking.";
      toast.error(msg);
    }
  };

  if (!user) return null;

  const upcoming = bookings.filter((b) => b.status === "upcoming").length;

  const tabs: { id: Tab; label: string; icon: typeof Calendar; count?: number }[] = [
    { id: "bookings", label: "Bookings", icon: Calendar, count: bookingsLoading ? undefined : bookings.length },
    { id: "favorites", label: "Favorites", icon: Heart, count: savedVenues.length },
    { id: "cards", label: "Cards", icon: CreditCard, count: cards.length },
    { id: "settings", label: "Settings", icon: UserCircle2 },
  ];

  return (
    <section className="mx-auto w-full max-w-6xl px-3 py-6 sm:px-4">
      <div className="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="self-start rounded-2xl border border-border/60 bg-card p-6 text-center shadow-soft lg:sticky lg:top-28">
          <div className="mx-auto grid size-24 place-items-center rounded-full bg-gradient-gold text-primary">
            <UserCircle2 className="size-14" />
          </div>
          <h2 className="mt-4 font-display text-xl font-semibold break-words">{user.name}</h2>
          <p className="mt-1 flex items-center justify-center gap-1 text-sm text-muted-foreground">
            <Mail className="size-3.5 shrink-0" /> <span className="break-all">{user.email}</span>
          </p>

          <nav className="mt-6 space-y-1 text-left">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium transition ${
                  tab === t.id ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-accent"
                }`}
              >
                <span className="flex items-center gap-2">
                  <t.icon className="size-4" /> {t.label}
                </span>
                {typeof t.count === "number" && (
                  <span className={`rounded-full px-2 text-xs ${tab === t.id ? "bg-white/20" : "bg-muted"}`}>{t.count}</span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 space-y-6">
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-soft sm:p-8">
            <h1 className="font-display text-2xl font-bold sm:text-3xl">Welcome back, {user.name.split(" ")[0]}.</h1>
            <p className="mt-2 text-muted-foreground">
              You have {upcoming} upcoming reservation{upcoming === 1 ? "" : "s"} and {savedVenues.length} saved venue{savedVenues.length === 1 ? "" : "s"}.
            </p>
          </div>

          {tab === "bookings" && (
            bookingsLoading ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
            ) : bookings.length === 0 ? (
              <Empty title="No bookings yet" body="When you reserve a venue it will appear here." cta="Find your next stay" to="/hotels" />
            ) : (
              <div className="space-y-4">
                {bookings.map((b) => (
                  <article key={b.id} className="flex flex-col gap-4 rounded-2xl border border-border/60 bg-card p-4 shadow-soft sm:flex-row">
                    {b.venueImage && <img src={resolveMediaUrl(b.venueImage)} alt={b.venueName} className="aspect-[4/3] w-full rounded-xl object-cover sm:w-48" />}
                    <div className="flex min-w-0 flex-1 flex-col gap-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-display text-lg font-semibold break-words">{b.venueName}</h3>
                          {b.venueLocation && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="size-3.5" /> {b.venueLocation}, Oyo State
                            </p>
                          )}
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
                          b.status === "upcoming" ? "bg-success/15 text-success" :
                          b.status === "cancelled" ? "bg-destructive/15 text-destructive" :
                          "bg-muted text-muted-foreground"
                        }`}>{b.status}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground sm:grid-cols-4">
                        <Field label="Check-in" value={new Date(b.startDate).toLocaleDateString()} />
                        <Field label="Check-out" value={new Date(b.endDate).toLocaleDateString()} />
                        <Field label="Nights" value={String(b.daysCount)} />
                        <Field label="Reference" value={b.bookingRef} />
                      </div>
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <span className="font-display text-base font-bold">{formatNaira(b.totalAmount)}</span>
                        <div className="flex gap-2">
                          <Link to="/venues/$venueId" params={{ venueId: b.venueId }}>
                            <Button size="sm" variant="outline">View venue</Button>
                          </Link>
                          {b.status === "upcoming" && (
                            <Button size="sm" variant="ghost" onClick={() => cancelBooking(b.id)}>
                              <X className="size-4" /> Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )
          )}

          {tab === "favorites" && (
            savedVenues.length === 0 ? (
              <Empty title="No saved venues" body="Tap the heart on any venue to save it for later." cta="Browse venues" to="/hotels" />
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                {savedVenues.map((v) => <VenueCard key={v.id} venue={v} />)}
              </div>
            )
          )}

          {tab === "cards" && (
            cardsLoading ? (
              <div className="flex h-40 items-center justify-center"><Loader2 className="size-5 animate-spin text-muted-foreground" /></div>
            ) : cards.length === 0 ? (
              <Empty title="No saved cards" body="Your saved payment cards will appear here." cta="Add a card" to="/booking" />
            ) : (
              <div className="space-y-4">
                {cards.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card p-4 shadow-soft sm:p-5">
                    <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                      <div className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                        <CreditCard className="size-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{c.cardName}</div>
                        <div className="truncate text-xs text-muted-foreground">{c.cardType} •••• {c.last4} — Expires {c.expiryMonth}/{c.expiryYear}</div>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      {c.isDefault ? (
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">Default</span>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setDefaultCard(c.id)}>Set default</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {tab === "settings" && (
            <div className="rounded-2xl border border-border/60 bg-card p-8 shadow-soft">
              <h2 className="font-display text-xl font-semibold">Account settings</h2>
              <p className="mt-1 text-sm text-muted-foreground">Profile editing, security and notification preferences will appear here.</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Field label="Full name" value={user.name} />
                <Field label="Email" value={user.email} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground break-words">{value}</div>
    </div>
  );
}

function Empty({ title, body, cta, to }: { title: string; body: string; cta: string; to: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center">
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      <Link to={to as "/hotels"}><Button className="mt-5">{cta}</Button></Link>
    </div>
  );
}
